<template>
    <div>
        <v-row>
            <v-col cols="6">
                <v-text-field
                    v-model="serverSearch"
                    outlined
                    dense
                    append-icon="mdi-search"
                    :label="$t('settings.server.search')"
                    single-line
                    hide-details
                >
                </v-text-field>
            </v-col>

            <v-col cols="2"></v-col>

            <v-col cols="4" class="text-right">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            color="blue-grey"
                            :loading="loading"
                            small
                            v-bind="attrs"
                            v-on="on"
                            @click="resetFileInput"
                        >
                            <v-icon dark dense> mdi-file-upload </v-icon>
                            <input
                                type="file"
                                ref="serverFile"
                                @change="
                                    (event) => readFile(event.target.files[0])
                                "
                                accept=".csv"
                                style="display: none"
                            />
                        </v-btn>
                    </template>
                    <span>{{ $t("common.file.uploadSettingsFromCSV") }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            color="blue-grey"
                            class="ml-2"
                            :loading="loading"
                            small
                            v-bind="attrs"
                            v-on="on"
                        >
                            <download-excel
                                :data="serverData"
                                :fields="csvFields"
                                style="display: inline"
                                type="csv"
                                name="ServerSettings.csv"
                                :escapeCsv="false"
                            >
                                <v-icon dark dense>
                                    mdi-microsoft-excel
                                </v-icon>
                            </download-excel>
                        </v-btn>
                    </template>
                    <span>{{ $t("common.file.downloadSettingsToCSV") }}</span>
                </v-tooltip>
                <v-btn
                    small
                    color="blue-grey"
                    class="ml-2"
                    @click="loadServer"
                    :loading="loading"
                    ><v-icon>mdi-refresh</v-icon></v-btn
                >

                <v-btn
                    small
                    color="primary"
                    class="ml-2"
                    :loading="loading"
                    v-on:click="toggleDialog('add')"
                    >{{ $t("common.form.add") }}</v-btn
                >
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-data-table
                    dense
                    :search="serverSearch"
                    :headers="serverHeader"
                    :items="serverData"
                    item-key="id"
                    class="elevation-0"
                    :loading="loading"
                    :items-per-page="10"
                    id="home-table"
                    :hide-default-footer="false"
                    :header-props="{ sortIcon: null }"
                    fixed-header
                    :footer-props="{
                        'items-per-page-text': $t('common.table.rowsPerPage'),
                    }"
                >
                    <template v-slot:item.action="{ item }">
                        <v-icon
                            dense
                            v-on:click="toggleDialog('update', item)"
                            :disabled="loading"
                            >mdi-pen</v-icon
                        >
                        &nbsp;
                        <v-icon
                            dense
                            @click="deleteConfirmation(item)"
                            :disabled="loading"
                            >mdi-delete</v-icon
                        >
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <DeleteConfirmDialog
            :confirmPopover="confirmPopover"
            :type="$t('common.table.server')"
            :name="selectedDeletion.server_name"
            :loading="loading"
            @close="
                () => {
                    confirmPopover = false;
                }
            "
            @confirm="deleteServer"
        ></DeleteConfirmDialog>

        <v-dialog
            v-model="dialog.visible"
            :fullscreen="dialog.fullscreen"
            width="500"
            :hide-overlay="true"
            :persistent="true"
        >
            <v-card class="pt-0">
                <v-app-bar
                    flat
                    color="rgba(0, 0, 0, 0)"
                    style="background: rgb(26, 57, 88)"
                >
                    <v-toolbar-title class="text-h6 white--text pl-0">{{
                        dialog.title
                    }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="white"
                        icon
                        @click="dialog.fullscreen = !dialog.fullscreen"
                        ><v-icon>mdi-arrow-expand</v-icon></v-btn
                    >
                    <v-btn color="white" icon @click="dialog.visible = false"
                        ><v-icon>mdi-close</v-icon></v-btn
                    >
                </v-app-bar>

                <v-card-actions>
                    <v-row>
                        <v-col
                            cols="12"
                            sm="12"
                            md="12"
                            lg="12"
                            xl="12"
                            v-if="dialog.type === 'add'"
                        >
                            <v-autocomplete
                                :label="$t('common.table.brokerName')"
                                v-model="dialog.broker_id"
                                :items="brokerData"
                                item-text="broker_name"
                                item-value="id"
                                auto-select-first
                                chips
                                clearable
                                deletable-chips
                                small-chips
                                hide-details
                            >
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12" sm="12" md="12" lg="12" xl="12">
                            <v-text-field
                                :label="$t('common.table.serverName')"
                                v-model="dialog.server_name"
                                hide-details="auto"
                                :rules="[rules.required]"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="12" md="12" lg="12" xl="12">
                            <v-text-field
                                :label="$t('common.table.description')"
                                v-model="dialog.description"
                                hide-details="auto"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="12" md="12" lg="12" xl="12">
                            <v-text-field
                                :label="$t('common.table.adapterID')"
                                v-model="dialog.adapter_id"
                                hide-details="auto"
                                :rules="[rules.required]"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="12" md="12" lg="6" xl="6">
                            <v-text-field
                                :label="$t('common.table.host')"
                                v-model="dialog.host"
                                hide-details="auto"
                                :rules="[rules.required]"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="12" md="12" lg="6" xl="6">
                            <v-text-field
                                :label="$t('common.table.port')"
                                v-model="dialog.port"
                                hide-details="auto"
                                :rules="[rules.required, rules.port]"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-card-actions>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="green darken-1"
                        text
                        @click="dialog.visible = false"
                        :disabled="dialog.loading"
                        >{{ $t("common.form.cancel") }}</v-btn
                    >
                    <v-btn
                        color="green darken-1"
                        text
                        @click="addBrokerServer"
                        :loading="loading"
                        v-if="dialog.type === 'add'"
                        ><span>{{ $t("common.form.add") }}</span></v-btn
                    >
                    <v-btn
                        color="green darken-1"
                        text
                        @click="updateBrokerServer"
                        :loading="loading"
                        v-if="dialog.type === 'update'"
                        ><span>{{ $t("common.form.update") }}</span></v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
        <UpdateConfirmation
            type="server"
            :addition="addition"
            :edition="edition"
            :header="serverHeader"
        />
    </div>
</template>
<script>
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import validator from "@assets/js/validator";
import DeleteConfirmDialog from "@components/ComponentDeleteConfirmDialog";
import { rules } from "@assets/js/validator";
import { snackbar } from "@components/mixins/snackbar";
import UpdateConfirmation from "./ComponentUpdateConfirmation.vue";

export default {
    mixins: [snackbar],
    components: { DeleteConfirmDialog, UpdateConfirmation },
    data() {
        return {
            serverSearch: "",
            dialog: {
                visible: false,
                fullscreen: false,
                title: this.$t("settings.server.Add"),
                type: "",
                server_name: "",
                description: "",
                broker_id: 1,
                host: "",
                port: 443,
                adapter_id: "",
            },
            rules: rules,
            confirmPopover: false,
            selectedDeletion: {},
            addition: [],
            edition: [],
        };
    },
    computed: {
        ...mapState("SettingsServer", [
            "serverData",
            "serverHeader",
            "loading",
            "updated",
            "csvFields",
        ]),
        ...mapState("SettingsBroker", ["brokerData"]),
    },
    methods: {
        ...mapActions("SettingsServer", [
            "getServerAction",
            "postServerAction",
            "putServerAction",
            "deleteServerAction",
        ]),

        /**
         * Toggle dialog to add broker server or update
         * @param   {[type]}  type  [type description]
         * @param   {[type]}  data  [data description]
         * @return  {[type]}        [return description]
         */
        toggleDialog(type, data) {
            this.dialog.visible = true;
            this.dialog.type = type;

            if (type === "add") {
                this.dialog.title = this.$t("settings.server.Add");
                this.dialog.server_name = null;
                this.dialog.description = "";
                this.dialog.broker_id = 1;
                this.dialog.host = null;
                this.dialog.port = 443;
                this.dialog.adapter_id = null;
            }

            if (type === "update") {
                this.dialog.title =
                    this.$t("settings.server.Update") +
                    " - " +
                    data.server_name;
                this.dialog.id = data.id;
                this.dialog.server_name = data.server_name;
                this.dialog.description = data.description;
                this.dialog.broker_id = data.broker_id;
                this.dialog.host = data.host;
                this.dialog.port = data.port;
                this.dialog.adapter_id = data.adapter_id;
            }
        },
        /**
         * Add broekr server
         * @return  {[type]}  [return description]
         */
        addBrokerServer() {
            if (
                validator.isAnyFieldEmpty([
                    this.dialog.server_name,
                    this.dialog.broker_id,
                    this.dialog.host,
                    this.dialog.port,
                    this.dialog.adapter_id,
                ])
            ) {
                this.snackBarDanger(
                    this.$t("settings.server.emptyFieldsWarningAdd")
                );
                return;
            }
            // console.log(this.dialog)
            this.postServerAction(this.dialog);
        },
        /**
         * Update broker server list
         * @return  {[type]}  [return description]
         */
        updateBrokerServer() {
            if (
                validator.isAnyFieldEmpty([
                    this.dialog.server_name,
                    this.dialog.host,
                    this.dialog.port,
                    this.dialog.adapter_id,
                ])
            ) {
                this.snackBarDanger(
                    this.$t("settings.server.emptyFieldsWarningUpdate")
                );
                return;
            }
            console.log(this.dialog);
            this.putServerAction(this.dialog);
        },
        loadServer() {
            this.getServerAction();
        },
        deleteConfirmation(item) {
            this.confirmPopover = true;
            this.selectedDeletion = item;
        },
        deleteServer() {
            this.deleteServerAction({ id: this.selectedDeletion.id });
        },
        readFile(fileInput) {
            var reader = new FileReader();
            reader.onload = () => {
                var csv = reader.result
                    .replaceAll('"""', "")
                    .replaceAll('"=""', "");
                var lines = csv.split("\r\n");
                var result = [];
                var headers = lines[0].split(",");
                if (
                    JSON.stringify(headers) ===
                    JSON.stringify(Object.keys(this.csvFields))
                ) {
                    for (var i = 1; i < lines.length; i++) {
                        if (lines[i] === "") {
                            break;
                        }
                        var obj = {};
                        var currentLine = lines[i].split(",");
                        for (var j = 0; j < headers.length; j++) {
                            obj[this.csvFields[headers[j]]] = currentLine[j];
                        }
                        result.push(obj);
                    }

                    this.addition = [];
                    this.edition = [];
                    let add = [];
                    let change = [];
                    result.map((item) => {
                        const found = this.serverData.find(
                            (data) => data.id.toString() === item.id
                        );
                        if (found) {
                            for (const attr in item) {
                                if (item[attr] !== found[attr].toString()) {
                                    change.push(item);
                                    break;
                                }
                            }
                        } else {
                            add.push(item);
                        }
                    });
                    if (change.length === 0 && add.length === 0) {
                        this.snackBarSuccess(
                            this.$t("message.allSettingsUpToDate")
                        );
                    } else {
                        this.addition = [...add];
                        this.edition = [...change];
                    }
                } else {
                    this.snackBarDanger(this.$t("message.invalidFile"));
                }
            };
            reader.readAsText(fileInput);
        },
        resetFileInput() {
            this.$refs.serverFile.value = "";
            this.$refs.serverFile.click();
        },
    },
    watch: {
        updated(nv) {
            if (nv) {
                this.dialog.visible = false;
                this.confirmPopover = false;
                this.loadServer();
            }
        },
    },
};
</script>
